
import createTransaction from '../api/createTransaction';
import { appConfig } from '../config/appConfig';

interface PaymentData {
  recipientName: string;
  recipientAddress: string;
  recipientAccountNumber: string;
  transferTitle: string;
  amount: string; // Explicitly defined as string
  userId: string;
  callbackSuccess: string;
  callbackFailure: string;
  linkExpirationDays: number;
  phone?: string; // Keep this for data storage but not for API request
  numberOfRooms?: number; // Add this to store the number of rooms
  roomAreas?: number[]; // Add this to store the room areas
  totalSquareArea?: number; // Add this to store the total square area
}

export const processPayment = async (paymentData: PaymentData): Promise<string> => {
  try {
    // Calculate 70% of the amount for the initial payment
    const fullAmount = parseFloat(paymentData.amount);
    const initialPaymentAmount = fullAmount * 0.7;
    
    // Format the amount to have 2 decimal places
    const formattedAmount = formatAmountWithTwoDecimals(initialPaymentAmount.toString());
    
    // Prepare payment data with AC in the transfer title
    // Use phone as userId but don't include phone field in the request
    const fizenPaymentData = {
      recipientName: paymentData.recipientName,
      recipientAddress: paymentData.recipientAddress,
      recipientAccountNumber: "PL59105014611000009106301634",
      transferTitle: `AC Order: ${paymentData.transferTitle} (zaliczka 70%)`,
      amount: formattedAmount, // This is a string
      callbackSuccess: window.location.origin + "/payment-success",
      callbackFailure: "https://fizen.com/error",
      userId: paymentData.phone || "PHONE_" + Date.now(), // Use phone as userId
      linkExpirationDays: paymentData.linkExpirationDays || 1
    };
    
    console.log("Prepared payment data:", fizenPaymentData);
    console.log("Room data for payment:", { 
      numberOfRooms: paymentData.numberOfRooms, 
      roomAreas: paymentData.roomAreas,
      totalSquareArea: paymentData.totalSquareArea
    });
    
    // Get offer type from transfer title
    let offerType: 'standard' | 'premium' | 'exclusive' = 'standard';
    if (paymentData.transferTitle.includes('Premium')) {
      offerType = 'premium';
    } else if (paymentData.transferTitle.includes('Exclusive')) {
      offerType = 'exclusive';
    }
    
    // Store order details in sessionStorage before redirecting
    sessionStorage.setItem('orderDetails', JSON.stringify({
      customerName: paymentData.recipientName,
      offerType: offerType,
      offerPrice: fullAmount,
      initialPayment: parseFloat(formattedAmount),
      remainingPayment: fullAmount * 0.3,
      email: paymentData.userId, // Keep original userId as email
      phone: paymentData.phone || "Not provided",
      address: paymentData.recipientAddress,
      numberOfRooms: paymentData.numberOfRooms, // Store number of rooms
      roomAreas: paymentData.roomAreas, // Store room areas
      totalSquareArea: paymentData.totalSquareArea // Store total square area
    }));
    
    let paymentUrl: string;
    
    // Use mock payment or real payment based on config
    if (appConfig.payment.useMockPayment) {
      console.log("Using MOCK payment processing");
      // Generate a mock payment URL that points to the success page with a transaction ID
      const mockTransactionId = "mock_" + Date.now();
      paymentUrl = `${window.location.origin}/payment-success?transactionId=${mockTransactionId}`;
    } else {
      console.log("Using REAL payment processing");
      // Call the actual payment API through our proxy
      paymentUrl = await createTransaction(fizenPaymentData);
    }
    
    return paymentUrl;
  } catch (error) {
    console.error('Error processing payment:', error);
    throw error; // Re-throw to let the calling component handle it
  }
};

// Helper function to format amount with two decimal places
const formatAmountWithTwoDecimals = (amount: string): string => {
  // Parse the amount to a number
  const numericAmount = parseFloat(amount);
  
  // Format with two decimal places
  return numericAmount.toFixed(2);
};
